/**
 * AI WebSocket Service
 * Handles streaming AI chat responses via WebSocket
 */

import { supabase } from '@/lib/supabase'

export interface AIStreamMessage {
  type: 'chunk' | 'done' | 'error'
  content?: string
  error?: string
  finishReason?: 'stop' | 'length' | 'content_filter' | 'tool_calls' | 'other' // 'length' means max_tokens reached
  usage?: {
    promptTokens: number
    completionTokens: number
    totalTokens: number
  }
}

type AIStreamCallback = (message: AIStreamMessage) => void
type ConnectionStatusCallback = (isConnected: boolean) => void

interface AIChatConnection {
  ws: WebSocket
  chatId: string
  reconnectAttempts: number
  reconnectTimeout: NodeJS.Timeout | null
}

class AIWebSocketService {
  private connections: Map<string, AIChatConnection> = new Map()
  private callbacks: Map<string, AIStreamCallback[]> = new Map()
  private statusCallbacks: Set<ConnectionStatusCallback> = new Set()
  private maxReconnectAttempts = 3
  private reconnectDelay = 2000

  async connect(
    chatId: string,
    onMessage: AIStreamCallback
  ): Promise<() => void> {
    // Add callback
    if (!this.callbacks.has(chatId)) {
      this.callbacks.set(chatId, [])
    }
    this.callbacks.get(chatId)!.push(onMessage)

    // Connect if not already connected
    if (!this.connections.has(chatId)) {
      await this.createConnection(chatId)
    }

    // Return cleanup function
    return () => {
      const callbacks = this.callbacks.get(chatId) || []
      const index = callbacks.indexOf(onMessage)
      if (index > -1) {
        callbacks.splice(index, 1)
      }
      if (callbacks.length === 0) {
        this.callbacks.delete(chatId)
        this.disconnect(chatId)
      }
    }
  }

  /**
   * Send chat request via WebSocket
   */
  sendChatRequest(
    chatId: string,
    request: {
      provider: 'openai' | 'anthropic';
      apiKey: string;
      messages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }>;
      model?: string;
      temperature?: number;
      maxTokens?: number;
    }
  ): boolean {
    const connection = this.connections.get(chatId)
    if (!connection) {
      console.warn(`No connection found for chat ${chatId}`)
      return false
    }

    if (!connection.ws) {
      console.warn(`No WebSocket found for chat ${chatId}`)
      return false
    }

    // Check if WebSocket is open or connecting (CONNECTING = 0, OPEN = 1)
    if (connection.ws.readyState === WebSocket.CONNECTING) {
      // Wait a bit and retry
      setTimeout(() => {
        if (connection.ws.readyState === WebSocket.OPEN) {
          try {
            connection.ws.send(JSON.stringify(request))
          } catch (error) {
            console.error('Error sending WebSocket message after retry:', error)
          }
        }
      }, 100)
      return true // Return true to indicate we're handling it
    }

    if (connection.ws.readyState === WebSocket.OPEN) {
      try {
        connection.ws.send(JSON.stringify(request))
        return true
      } catch (error) {
        console.error('Error sending WebSocket message:', error)
        return false
      }
    }

    console.warn(`WebSocket not ready for chat ${chatId}, state: ${connection.ws.readyState}`)
    return false
  }

  private async createConnection(chatId: string) {
    // Get token from Supabase session
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) {
      console.error('No auth token found for AI WebSocket')
      return
    }

    const token = session.access_token
    const wsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:3000'
    const url = `${wsUrl}/api/v1/ai/chat/stream?chatId=${chatId}&token=${token}`

    const ws = new WebSocket(url)
    const connection: AIChatConnection = {
      ws,
      chatId,
      reconnectAttempts: 0,
      reconnectTimeout: null,
    }

    // Store connection immediately (before onopen) so sendChatRequest can find it
    // This allows sendChatRequest to work even if called before onopen fires
    this.connections.set(chatId, connection)

    ws.onopen = () => {
      console.log(`AI WebSocket connected for chat ${chatId}`)
      connection.reconnectAttempts = 0
      this.notifyStatusChange(true)
      
      // Ensure connection is still in map (in case it was removed)
      if (!this.connections.has(chatId)) {
        this.connections.set(chatId, connection)
      }
    }

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as AIStreamMessage
        const callbacks = this.callbacks.get(chatId) || []
        callbacks.forEach((callback) => callback(message))
      } catch (error) {
        console.error('Failed to parse AI WebSocket message:', error)
      }
    }

    ws.onerror = (error) => {
      console.error(`AI WebSocket error for chat ${chatId}:`, error)
    }

    ws.onclose = (event) => {
      console.log(`AI WebSocket disconnected for chat ${chatId}`, {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      })

      this.connections.delete(chatId)
      this.notifyStatusChange(false)

      // Attempt reconnect if there are still callbacks
      const callbacks = this.callbacks.get(chatId)
      if (callbacks && callbacks.length > 0 && !event.wasClean) {
        this.attemptReconnect(chatId, connection)
      }
    }

    // Connection is already set above, before onopen
  }

  private attemptReconnect(chatId: string, oldConnection: AIChatConnection) {
    if (oldConnection.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn(`Max reconnect attempts reached for AI chat ${chatId}`)
      return
    }

    oldConnection.reconnectAttempts++
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, oldConnection.reconnectAttempts - 1),
      30000
    )

    oldConnection.reconnectTimeout = setTimeout(async () => {
      const callbacks = this.callbacks.get(chatId)
      if (callbacks && callbacks.length > 0) {
        await this.createConnection(chatId)
      }
    }, delay)
  }

  disconnect(chatId: string) {
    const connection = this.connections.get(chatId)
    if (connection) {
      if (connection.reconnectTimeout) {
        clearTimeout(connection.reconnectTimeout)
      }
      connection.ws.close()
      this.connections.delete(chatId)
    }
  }

  /**
   * Stop streaming for a chat (send stop signal and disconnect)
   */
  stopStreaming(chatId: string) {
    const connection = this.connections.get(chatId)
    if (connection && connection.ws) {
      try {
        // Send stop message if connection is open
        if (connection.ws.readyState === WebSocket.OPEN) {
          connection.ws.send(JSON.stringify({ type: 'stop' }))
        }
        // Close the connection
        connection.ws.close()
      } catch (error) {
        console.error('Error stopping stream:', error)
      }
      // Clean up
      if (connection.reconnectTimeout) {
        clearTimeout(connection.reconnectTimeout)
      }
      this.connections.delete(chatId)
    }
  }

  isConnected(chatId: string): boolean {
    const connection = this.connections.get(chatId)
    return connection !== undefined && connection.ws !== undefined && connection.ws.readyState === WebSocket.OPEN
  }

  onStatusChange(callback: ConnectionStatusCallback): () => void {
    this.statusCallbacks.add(callback)
    return () => {
      this.statusCallbacks.delete(callback)
    }
  }

  private notifyStatusChange(isConnected: boolean) {
    this.statusCallbacks.forEach((callback) => callback(isConnected))
  }
}

// Singleton instance
let instance: AIWebSocketService | null = null

export function getAIWebSocketService(): AIWebSocketService {
  if (!instance) {
    instance = new AIWebSocketService()
  }
  return instance
}

export default getAIWebSocketService()
